import {
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
  KuxMenuItem,
  KuxFilteredCategoriesDTO,
  parseMenuItemFromCategory,
  categoryTreeItemsMap,
  TypeOfApplicationLink,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { container } from 'tsyringe';

import { errorHandling } from '../../../utils/error-handling';
import type { DmsStore } from '../../dms/dms-store';
import { LinkAsAsset } from '../../entities';
import { createLinkAsAssetByKuxMenuItem } from '../../entities/entities.service';
import { ErrorPriority } from '../../error';
import type { TransactionStore } from '../../transaction/transaction-store';
import { CategoryChildren } from '../category-children';
import { CategoryStore } from '../category-store';
import { isNewSubtypeSupported } from '../widgets';

/**
 * Store for root main menu category. Stores array of menu items.
 * @category Store
 */
export class MenuStore extends CategoryStore {
  static moduleName = 'MenuStore';

  @observable isReady = false;
  /**
   * list of menu items
   */
  @observable private _items: LinkAsAsset[] = [];

  type: UIDAStructureTypeEnum.menu = UIDAStructureTypeEnum.menu;
  subtype: UIDAPresentationTypeEnum.menu = UIDAPresentationTypeEnum.menu;
  // TODO
  displayAppIcon = false;
  displayClockIcon = true;
  parentalPinProtected = false;
  subMenus: KuxFilteredCategoriesDTO[] = [];

  private dmsStore: DmsStore;

  constructor(
    categoryItem: KuxFilteredCategoriesDTO,
    parentCategoryTree: KuxFilteredCategoriesDTO,
    children: CategoryChildren[] = [],
  ) {
    super(categoryItem, parentCategoryTree);
    this.children = children;
    this.subMenus = categoryItem.children;
    this.dmsStore = container.resolve<DmsStore>('dmsStore');
  }

  private get transactionStore() {
    return container.resolve<TransactionStore>('transactionStore');
  }

  @computed
  public get items(): LinkAsAsset[] {
    if (this.transactionStore.isAdultEntitled) {
      return this._items;
    }

    return this._items.filter((item) => !item.isAdult);
  }

  @action
  @errorHandling({ type: ErrorPriority.high })
  init() {
    if (this.isReady) {
      return;
    }
    const menuCategories = (this.subMenus && this.subMenus[0]?.children) || [];
    this._items = menuCategories
      .filter((item) => {
        const { inAppLink, typeOfAppLink } = item.link;

        if (typeOfAppLink !== TypeOfApplicationLink.page && typeOfAppLink !== TypeOfApplicationLink.grid) {
          return true;
        }

        if (!inAppLink) {
          return false;
        }

        const category = categoryTreeItemsMap.get(inAppLink);

        if (!category) {
          return false;
        }

        if (category.type === UIDAStructureTypeEnum.page && category.subtype === UIDAPresentationTypeEnum.container) {
          return category.children.some(
            (referenceId) => categoryTreeItemsMap.get(referenceId)?.type === UIDAStructureTypeEnum.widget,
          );
        }

        return true;
      })
      .map((category) => {
        return parseMenuItemFromCategory(category, {
          includePresentationType: category.subtype && isNewSubtypeSupported(category.subtype),
        });
      })
      .filter(Boolean)
      .map((item) => createLinkAsAssetByKuxMenuItem(item as KuxMenuItem));
    this.isReady = true;
  }
}
